import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as router from '@attain-apps/fe/web/main/lib/router'

import * as state from '../../../../../../../../state'

import * as components from './components'

const ADD_RULE_SLIDE_OUT_TITLE = 'Add Rule'
/**
 * Only used if we can't get the rule's name
 */
const EDIT_RULE_SLIDE_OUT_FALLBACK_TITLE = 'Edit Rule'

const SUBMIT_BTN_CREATE_LABEL = 'Create'
const SUBMIT_BTN_UPDATE_LABEL = 'Update'

@ngCore.Component({
  imports: [
    components.adjustmentTab.Component,
    components.conditionsTab.Component,
    ds.button.Component,
    ds.tabs.integratedHorizontal.Component,
    ds.tabs.tab.Directive,
    ds.overlay.slideout.Component,
  ],
  selector: 'atn-rate-rule-slideout',
  standalone: true,
  styleUrls: ['./rule-slideout.component.scss'],
  templateUrl: './rule-slideout.component.html',
})
export class AtnRuleSlideoutComponent {
  /** @see {@link ds.Color} */
  public readonly PredefinedStyleEnum = ds.color.Legacy.PredefinedStyle

  /** @see {@link state.rate.gl.rules.Collection} */
  private readonly _ruleEc = ngCore.inject(state.rate.gl.rules.Collection)
  /** @see {@link state.rate.gl.records.Collection} */
  private readonly _recordsEc = ngCore.inject(state.rate.gl.records.Collection)
  /** @see {@link state.me.Feature} */
  private readonly _meFeature = ngCore.inject(state.me.Feature)

  private readonly _router = ngCore.inject(router.Router)

  /**
   * Indicates a rule is currently being edited
   */
  public readonly isEditingSg =
    this._ruleEc.plugins.currentEntityPlugin.feature.selectors.isEditing()
      .asSignal

  /**
   * Title of the slideout
   */
  public readonly titleSg = ngCore.computed(() => {
    if (
      this._ruleEc.plugins.currentEntityPlugin.feature.selectors
        .isCreating()
        .asSignal()
    ) {
      return ADD_RULE_SLIDE_OUT_TITLE
    }
    return (
      this._ruleEc.plugins.currentEntityPlugin.feature.selectors
        .getEditedEntity()
        .asSignal()?.name ?? EDIT_RULE_SLIDE_OUT_FALLBACK_TITLE
    )
  })

  /**
   * Represents the label of the slide-out submit button
   */
  public readonly submitButtonLabelSg = ngCore.computed(() =>
    this._ruleEc.plugins.currentEntityPlugin.feature.selectors
      .isCreating()
      .asSignal()
      ? SUBMIT_BTN_CREATE_LABEL
      : SUBMIT_BTN_UPDATE_LABEL,
  )

  /* -------------------------------------------------------------------------- */
  /*                                  Handlers                                  */
  /* -------------------------------------------------------------------------- */

  /**
   * Discards an active `creation` / `updating` of a rule and navigates to the
   * {@link ents.extra.prebuilt.Page.Id.RateGl} page
   */
  public cancelBtnClickHandler = () => {
    const source = 'RuleSlideout.Footer.Button.Cancel.Clicked'
    this._ruleEc.plugins.editingPlugin.feature.actions.discard
      .from(source)
      .dispatch({ current: true })
    this._navigate(source, ents.extra.prebuilt.AppPageAlias.Rules)
  }

  /**
   * Initiates the process of `creating` or `updating` a rule in the database
   * and navigates to the {@link ents.extra.prebuilt.Page.Id.RateGl} page
   */
  public submitBtnClickHandler = () => {
    const source = 'RuleSlideout.Footer.Button.Submit.Clicked'
    this._ruleEc.plugins.editingPlugin.feature.actions.submit
      .from(source)
      .dispatch({ current: true })
    this._navigate(source, ents.extra.prebuilt.AppPageAlias.Rules)
  }

  /**
   * Navigates to the {@link ents.extra.prebuilt.Page.Id.RateGl} page and
   * applies filters for the GL
   */
  public testBtnClickHandler(): void {
    const source = 'RuleSlideout.Footer.Button.Test.Clicked'

    this._navigate(source, ents.extra.prebuilt.AppPageAlias.Gl)

    const filter = this._ruleEc.plugins.currentEntityPlugin.feature.selectors
      .getEditedEntity()
      .asSignal()?.filter

    if (filter) {
      this._recordsEc.plugins.currentListPlugin.appliedParams.actions.setFilters
        .from(source)
        .dispatch(filter)
    }
  }

  /**
   * Discards any changes to a {@link models.rate.GlMappingRule}
   */
  public closeBtnClickHandler = (): void => {
    this._ruleEc.plugins.editingPlugin.feature.actions.discard
      .from('RuleSlideout.Header.Button.Close.Clicked')
      .dispatch({ current: true })
  }

  private _navigate(
    source: string,
    target: ents.extra.prebuilt.AppPageAlias,
  ): void {
    const url = this._meFeature.contexts.selectors
      .getUrlForLink({ goto: { navigationId: target } })
      .asSignal()
    if (url) {
      this._router.actions.navigate.from(source).dispatch({ url })
    }
  }
}
